import { useModel as _useModel, mergeModels as _mergeModels, defineComponent as _defineComponent } from 'vue'
import { renderSlot as _renderSlot, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, createVNode as _createVNode, mergeProps as _mergeProps, unref as _unref, createTextVNode as _createTextVNode, withCtx as _withCtx, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createSlots as _createSlots, createBlock as _createBlock } from "vue"

const _hoisted_1 = ["name", "value"]
const _hoisted_2 = ["disabled", "onClick"]

import { computed, nextTick, useTemplateRef, ref } from 'vue';
import { useResizeObserver } from '@vueuse/core';
import type { SelectItem, SelectItemSeparator } from '../../lib/list-selection/types';
import ActionButton from '@/components/base-combobox/action-button.vue';
import { trlMessage } from '../../lib/i18n';
import BaseIcon from '../../legacy/ui/base-icon/icon.vue';
import BaseDropdown from '../../legacy/ui/base-dropdown/base-dropdown.vue';
import DropdownMenuLayout from '../../legacy/ui/dropdown-menu-layout/dropdown-menu-layout.vue';
import BaseSelectList from './base-select-list.vue';

type Props = {
  items: Array<Item | SelectItemSeparator>;
  placeholder?: string;
  showCheckbox?: boolean;
  clearable?: boolean;
  invalid?: boolean;
  multiline?: boolean;
  size?: string;
  disabled?: boolean;
  multiple?: Multiple;
};

type Id = Item['id'];

type ModelValue<Multiple extends boolean> = Multiple extends true
  ? Id[]
  : Id;


export default /*@__PURE__*/_defineComponent({
  ...{
  inheritAttrs: false
},
  __name: 'base-select-dropdown',
  props: /*@__PURE__*/_mergeModels({
    items: {},
    placeholder: {},
    showCheckbox: { type: Boolean },
    clearable: { type: Boolean },
    invalid: { type: Boolean },
    multiline: { type: Boolean },
    size: {},
    disabled: { type: Boolean },
    multiple: {}
  }, {
    "modelValue": {
  default: []
},
    "modelModifiers": {},
  }),
  emits: /*@__PURE__*/_mergeModels(["dropdownChange", "clear", "itemClick"], ["update:modelValue"]),
  setup(__props: any, { emit: __emit }) {



const props = __props;
const emit = __emit;

const value = _useModel<ModelValue<Multiple>>(__props, "modelValue");

const itemsMap = computed(() => {
  return new Map(
    props.items
      .filter((item): item is Item => item.type !== 'separator')
      .map((e) => [e.id, e])
  );
});

const base = useTemplateRef('base');
const dropdownSize = ref<number | 'large'>('large');

useResizeObserver(base, ([{ target }]) => {
  if (target.offsetWidth) {
    dropdownSize.value = target.offsetWidth;
  }
});

// Native form support
const hiddenInputValue = computed(() => {
  return value.value ?? undefined;
});

function handleItemClick(item: Item, hide: () => void) {
  emit('itemClick', item);
  if (props.multiple) {
    return;
  }
  hide();
  // Return focus to the trigger after picking an option. Otherwise Safari's
  // focus-fixup moves focus to the nearest input once the dropdown layer is
  // removed from the DOM (https://bugs.webkit.org/show_bug.cgi?id=278455).
  nextTick(() => base.value?.focus());
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(BaseDropdown, {
    onChange: _cache[2] || (_cache[2] = ($event: any) => (emit('dropdownChange')))
  }, {
    default: _withCtx(({ toggle: dropdownToggle }) => [
      _renderSlot(_ctx.$slots, "toggle", { toggle: dropdownToggle }, () => [
        _createElementVNode("input", {
          type: "hidden",
          name: _ctx.$attrs.name,
          value: hiddenInputValue.value
        }, null, 8, _hoisted_1),
        _createElementVNode("button", _mergeProps({
          ref_key: "base",
          ref: base,
          class: [
            _ctx.$style.button, {
              [_ctx.$style.disabled]: __props.disabled,
              [_ctx.$style.invalid]: __props.invalid
            }
          ]
        }, _ctx.$attrs, {
          type: "button",
          disabled: __props.disabled,
          onClick: dropdownToggle
        }), [
          _createElementVNode("span", {
            class: _normalizeClass(_ctx.$style.text)
          }, _toDisplayString(itemsMap.value.get(value.value)?.name || __props.placeholder), 3),
          _createElementVNode("span", {
            class: _normalizeClass(_ctx.$style.iconWrapper)
          }, [
            _renderSlot(_ctx.$slots, "icon", {}, () => [
              _createVNode(BaseIcon, { type: "chevron-down-24" })
            ])
          ], 2)
        ], 16, _hoisted_2)
      ])
    ]),
    content: _withCtx(({ hide }) => [
      _createVNode(DropdownMenuLayout, {
        size: __props.size || dropdownSize.value
      }, {
        "sub-heading": _withCtx(() => [
          (__props.clearable)
            ? (_openBlock(), _createElementBlock("div", {
                key: 0,
                class: _normalizeClass(_ctx.$style.sub)
              }, [
                _createVNode(ActionButton, {
                  "data-qa": "reset",
                  onClick: _cache[0] || (_cache[0] = ($event: any) => (emit('clear')))
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(_unref(trlMessage)('autocomplete.reset')), 1)
                  ]),
                  _: 1
                })
              ], 2))
            : _createCommentVNode("", true)
        ]),
        content: _withCtx(() => [
          _createVNode(BaseSelectList, {
            modelValue: value.value,
            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((value).value = $event)),
            gap: 0,
            items: __props.items,
            multiple: __props.multiple,
            multiline: __props.multiline,
            "show-checkbox": __props.showCheckbox,
            class: _normalizeClass(_ctx.$style.select),
            onItemClick: ($event: any) => (handleItemClick($event, hide))
          }, _createSlots({
            icon: _withCtx(({ item }) => [
              _renderSlot(_ctx.$slots, "item-icon", { item: item })
            ]),
            _: 2
          }, [
            (_ctx.$slots.item)
              ? {
                  name: "item",
                  fn: _withCtx(({ item, index, onHover, toggle }) => [
                    _renderSlot(_ctx.$slots, "item", {
                      item: item,
                      index: index,
                      onHover: onHover,
                      select: () => { toggle(item); handleItemClick(item, hide); }
                    })
                  ]),
                  key: "0"
                }
              : undefined
          ]), 1032, ["modelValue", "items", "multiple", "multiline", "show-checkbox", "class", "onItemClick"])
        ]),
        _: 2
      }, 1032, ["size"])
    ]),
    _: 3
  }))
}
}

})